import React, { useEffect, useImperativeHandle, useState, forwardRef } from "react"
import PropTypes from "prop-types"
import { Table, theme } from "antd"

import { InvestmentShape } from "@components/Investment/shapes"

import useAmountByKey from "./helpers/useAmountByKey"
import useColumns from "./helpers/useColumns"
import useItems from "./helpers/useItems"
import BeneficiariesTableSummary from "./BeneficiariesTableSummary"

const BeneficiariesTable = forwardRef(({ investments, distributionFrequency = undefined, shouldRoundCents = false }, ref) => {
  const { token } = theme.useToken()

  const [ selectedRowKeys, setSelectedRowKeys ] = useState([])

  const items = useItems(investments)
  const { amountByKey, customAmountByKey, setCustomAmountByKey } = useAmountByKey(items, distributionFrequency, shouldRoundCents)

  const columns = useColumns(amountByKey, customAmountByKey, setCustomAmountByKey, selectedRowKeys)

  useImperativeHandle(ref, () => ({
    getSelectedDistributions: () =>
      items
        .filter(({ key }) => selectedRowKeys.includes(key))
        .map(item => ({ ...item, amount: amountByKey[item.key] })),
    getTotalAmount: () =>
      selectedRowKeys
        .map(key => amountByKey[key])
        .reduce((sum, value) => sum + (Number(value) || 0), 0),
  }), [ items, selectedRowKeys, amountByKey ])

  useEffect(() => {
    setSelectedRowKeys(items.map(({ key }) => key))
  }, [ items ])

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  }

  const tableProps = {
    size: "small",
    rowKey: "key",
    bordered: true,
    dataSource: items,
    pagination: false,
    columns,
    rowSelection,
    onRow: ({ key }) => ({
      style: selectedRowKeys.includes(key)
        ? undefined
        : {
          backgroundColor: token.colorFillAlter,
          color: token.colorTextDisabled,
        },
    }),
    summary: () => (
      <BeneficiariesTableSummary
        amountByKey={amountByKey}
        selectedRowKeys={selectedRowKeys}
      />
    ),
  }

  return (
    <Table {...tableProps} />
  )
})

BeneficiariesTable.displayName = "BeneficiariesTable"

BeneficiariesTable.propTypes = {
  investments: PropTypes.arrayOf(InvestmentShape).isRequired,
  distributionFrequency: PropTypes.string,
  shouldRoundCents: PropTypes.bool,
}

BeneficiariesTable.defaultProps = {
  distributionFrequency: undefined,
  shouldRoundCents: false,
}

export default BeneficiariesTable
